import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileArchive, Users } from "lucide-react";
import { useMe } from "../App";
import { api, qs, startDownload, useApi } from "../lib/api";
import { bytes, fullDate, when } from "../lib/format";
import { ExecutableWarning, ZipDialog } from "../components/dialogs";
import { FileIcon } from "../components/icons";
import { Button, Empty, ErrorNote, Spinner, toast } from "../components/ui";

interface Item {
  owner: string;
  ownerName: string;
  ownerEmail: string;
  path: string;
  name: string;
  isFolder: boolean;
  size: number | null;
  modified: string | null;
  sharedAt: string;
  isNew: boolean;
}

export default function Shared() {
  const { me, refreshMe } = useMe();
  const shared = useApi<{ items: Item[] }>("/shared");
  const [zip, setZip] = useState<Item | null>(null);
  const [exe, setExe] = useState<{ name: string; url: string; sharedBy: { name: string; email: string } | null } | null>(null);

  // Once the list has loaded (with its "New" marks), clear the nav badge; the marks stay until the next visit.
  const loaded = Boolean(shared.data);
  useEffect(() => {
    if (!loaded || !me.newShares) return;
    void api("/shared/seen", { body: {} }).then(refreshMe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  const download = async (i: Item) => {
    try {
      const r = await api<{ url: string; executable: boolean; sharedBy: { name: string; email: string } | null }>(`/download?${qs({ owner: i.owner, path: i.path })}`);
      if (r.executable) setExe({ name: i.name, url: r.url, sharedBy: r.sharedBy });
      else startDownload(r.url);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  const groups = new Map<string, Item[]>();
  for (const i of shared.data?.items ?? []) groups.set(i.owner, [...(groups.get(i.owner) ?? []), i]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5">
      <h1 className="text-lg font-semibold">Shared with me</h1>
      <p className="text-sm text-muted mt-1">Files and folders other people at Intellias shared with you. View and download only.</p>
      <div className="mt-5 space-y-6">
        {shared.error ? (
          <ErrorNote>{shared.error}</ErrorNote>
        ) : !shared.data ? (
          <Spinner />
        ) : !groups.size ? (
          <div className="border border-dashed border-line rounded-xl">
            <Empty icon={<Users className="size-8" />} title="Nothing shared with you yet">
              When someone shares a file or folder with you, it shows up here.
            </Empty>
          </div>
        ) : (
          [...groups.values()].map((items) => (
            <section key={items[0].owner}>
              <h2 className="text-sm font-semibold text-ink-2 mb-2">
                {items[0].ownerName} <span className="font-normal text-muted">· {items[0].ownerEmail}</span>
              </h2>
              <ul className="border border-line rounded-xl bg-surface divide-y divide-line overflow-hidden">
                {items.map((i) => (
                  <li key={i.path} className="flex items-center gap-3 px-4 py-3">
                    <FileIcon name={i.name} folder={i.isFolder} />
                    <div className="flex-1 min-w-0">
                      {i.isFolder ? (
                        <Link to={`/shared/browse?${qs({ owner: i.owner, path: i.path })}`} className="text-sm font-medium hover:underline truncate block">
                          {i.name}
                        </Link>
                      ) : (
                        <button onClick={() => download(i)} className="text-sm font-medium hover:underline truncate block max-w-full text-left">
                          {i.name}
                        </button>
                      )}
                      <div className="text-xs text-muted" title={fullDate(i.sharedAt)}>
                        {i.isNew && <span className="inline-block rounded-full bg-accent text-accent-ink px-1.5 mr-1.5">New</span>}
                        Shared {when(i.sharedAt)}
                        {!i.isFolder && ` · ${bytes(i.size)}`}
                      </div>
                    </div>
                    {i.isFolder ? (
                      <Button variant="ghost" onClick={() => setZip(i)} aria-label={`Download ${i.name} as ZIP`}>
                        <FileArchive className="size-4" /> <span className="hidden sm:inline">ZIP</span>
                      </Button>
                    ) : (
                      <Button variant="ghost" onClick={() => download(i)} aria-label={`Download ${i.name}`}>
                        <Download className="size-4" /> <span className="hidden sm:inline">Download</span>
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
      {zip && <ZipDialog owner={zip.owner} paths={[zip.path]} onClose={() => setZip(null)} />}
      {exe && <ExecutableWarning name={exe.name} sharedBy={exe.sharedBy} onConfirm={() => startDownload(exe.url)} onClose={() => setExe(null)} />}
    </div>
  );
}
